import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Editor } from '@tinymce/tinymce-react';
import { CheckCircle2, Download, Loader2, Pencil, Plus, Send, Trash2, Upload, XCircle } from 'lucide-react';
import {
  ContentPage,
  GuidanceEntry,
  LanguagePack,
  PlatformSettingsApi,
  UpdateAppSettingsData,
  UpdateEmailSettingsData,
  UpdateGeneralSettingsData,
  UpdateSubscriptionSettingsData,
} from '@/services/platform-settings.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { DataTable } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

const TABS = ['General', 'App', 'Subscription', 'Email', 'Content Pages', 'Guidance', 'Languages'] as const;
type Tab = (typeof TABS)[number];

// ───── Logo upload field — 4 named slots, reused for each ────────────────
function LogoUploadField({ label, url, onChange, readOnly }: { label: string; url: string | null; onChange: (url: string) => void; readOnly?: boolean }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function handlePick(file: File) {
    setUploading(true);
    setError(undefined);
    try {
      const res = await PlatformSettingsApi.upload(file);
      onChange(res.url);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      <div className="flex items-center gap-3">
        {url ? (
          <img src={url} alt={label} className="h-12 w-auto rounded border border-slate-200 bg-white object-contain p-1" />
        ) : (
          <div className="flex h-12 w-16 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">None</div>
        )}
        {!readOnly && (
          <label className="btn-secondary flex cursor-pointer items-center gap-1.5 !py-1.5 text-xs">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? 'Uploading…' : url ? 'Replace' : 'Upload'}
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => e.target.files?.[0] && handlePick(e.target.files[0])} />
          </label>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ───── General tab ─────────────────────────────────────────────────────
function GeneralTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['platform-settings', 'general'], queryFn: PlatformSettingsApi.getGeneral });
  const [form, setForm] = useState<UpdateGeneralSettingsData>({});

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const save = useMutation({
    mutationFn: () => PlatformSettingsApi.updateGeneral(form),
    onSuccess: (updated) => {
      qc.setQueryData(['platform-settings', 'general'], updated);
      qc.invalidateQueries({ queryKey: ['platform-settings', 'general'] });
    },
  });

  function set<K extends keyof UpdateGeneralSettingsData>(key: K, value: UpdateGeneralSettingsData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  if (isLoading || !data) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="System Name">
          <Input value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} disabled={readOnly} />
        </Field>
        <Field label="Theme Color">
          <div className="flex items-center gap-2">
            <Input type="color" className="!w-14 !p-1" value={form.themeColor ?? '#22577A'} onChange={(e) => set('themeColor', e.target.value)} disabled={readOnly} />
            <Input value={form.themeColor ?? ''} onChange={(e) => set('themeColor', e.target.value)} disabled={readOnly} />
          </div>
        </Field>
        <Field label="Timezone">
          <Input value={form.timezone ?? ''} onChange={(e) => set('timezone', e.target.value)} placeholder="Asia/Kolkata" disabled={readOnly} />
        </Field>
        <Field label="Mobile">
          <Input value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} disabled={readOnly} />
        </Field>
        <Field label="Date Format">
          <Select value={form.dateFormat ?? 'd-m-Y'} onChange={(e) => set('dateFormat', e.target.value)} disabled={readOnly}>
            <option value="d-m-Y">DD-MM-YYYY</option>
            <option value="m-d-Y">MM-DD-YYYY</option>
            <option value="Y-m-d">YYYY-MM-DD</option>
          </Select>
        </Field>
        <Field label="Time Format">
          <Select value={form.timeFormat ?? 'h:i A'} onChange={(e) => set('timeFormat', e.target.value)} disabled={readOnly}>
            <option value="h:i A">12-hour (h:i A)</option>
            <option value="H:i">24-hour (H:i)</option>
          </Select>
        </Field>
      </div>

      <Field label="Tagline">
        <Input value={form.tagline ?? ''} onChange={(e) => set('tagline', e.target.value)} disabled={readOnly} />
      </Field>
      <Field label="Address">
        <Textarea rows={2} value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} disabled={readOnly} />
      </Field>
      <Field label="Hero Description">
        <Textarea rows={3} value={form.heroDescription ?? ''} onChange={(e) => set('heroDescription', e.target.value)} disabled={readOnly} />
      </Field>

      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Branding</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <LogoUploadField label="Horizontal Logo" url={form.horizontalLogoUrl ?? null} onChange={(url) => set('horizontalLogoUrl', url)} readOnly={readOnly} />
          <LogoUploadField label="Vertical Logo" url={form.verticalLogoUrl ?? null} onChange={(url) => set('verticalLogoUrl', url)} readOnly={readOnly} />
          <LogoUploadField label="Favicon" url={form.faviconUrl ?? null} onChange={(url) => set('faviconUrl', url)} readOnly={readOnly} />
          <LogoUploadField label="Login Page Logo" url={form.loginPageLogoUrl ?? null} onChange={(url) => set('loginPageLogoUrl', url)} readOnly={readOnly} />
        </div>
      </div>

      {!readOnly && (
        <>
          {save.error && <p className="text-sm text-red-600">{errMsg(save.error)}</p>}
          {save.isSuccess && <p className="text-sm text-green-700">Saved.</p>}
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save General Settings'}
          </button>
        </>
      )}
    </div>
  );
}

// ───── App Settings tab ──────────────────────────────────────────────
function AppTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['platform-settings', 'app'], queryFn: PlatformSettingsApi.getApp });
  const [form, setForm] = useState<UpdateAppSettingsData>({});

  useEffect(() => {
    if (data) {
      setForm({
        appLink: data.appLink ?? undefined,
        iosAppLink: data.iosAppLink ?? undefined,
        appVersion: data.appVersion ?? undefined,
        iosAppVersion: data.iosAppVersion ?? undefined,
        forceAppUpdate: data.forceAppUpdate,
        appMaintenance: data.appMaintenance,
        teacherAppLink: data.teacherAppLink ?? undefined,
        teacherIosAppLink: data.teacherIosAppLink ?? undefined,
        teacherAppVersion: data.teacherAppVersion ?? undefined,
        teacherIosAppVersion: data.teacherIosAppVersion ?? undefined,
        teacherForceAppUpdate: data.teacherForceAppUpdate,
        teacherAppMaintenance: data.teacherAppMaintenance,
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => PlatformSettingsApi.updateApp(form),
    onSuccess: (updated) => qc.setQueryData(['platform-settings', 'app'], updated),
  });

  function set<K extends keyof UpdateAppSettingsData>(key: K, value: UpdateAppSettingsData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  if (isLoading || !data) return <p className="text-sm text-slate-500">Loading…</p>;

  function AppBlock({ prefix, title }: { prefix: ''; title: string } | { prefix: 'teacher'; title: string }) {
    const k = (suffix: string) => (prefix ? (`${prefix}${suffix[0].toUpperCase()}${suffix.slice(1)}` as keyof UpdateAppSettingsData) : (suffix as keyof UpdateAppSettingsData));
    return (
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Android App Link">
            <Input value={(form[k('appLink')] as string) ?? ''} onChange={(e) => set(k('appLink'), e.target.value)} placeholder="https://play.google.com/…" disabled={readOnly} />
          </Field>
          <Field label="iOS App Link">
            <Input value={(form[k('iosAppLink')] as string) ?? ''} onChange={(e) => set(k('iosAppLink'), e.target.value)} placeholder="https://apps.apple.com/…" disabled={readOnly} />
          </Field>
          <Field label="Android App Version">
            <Input value={(form[k('appVersion')] as string) ?? ''} onChange={(e) => set(k('appVersion'), e.target.value)} placeholder="1.0.0" disabled={readOnly} />
          </Field>
          <Field label="iOS App Version">
            <Input value={(form[k('iosAppVersion')] as string) ?? ''} onChange={(e) => set(k('iosAppVersion'), e.target.value)} placeholder="1.0.0" disabled={readOnly} />
          </Field>
        </div>
        <div className="mt-3 flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!form[k('forceAppUpdate')]} onChange={(e) => set(k('forceAppUpdate'), e.target.checked as any)} disabled={readOnly} />
            Force App Update
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!form[k('appMaintenance')]} onChange={(e) => set(k('appMaintenance'), e.target.checked as any)} disabled={readOnly} />
            Maintenance Mode
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-8">
      <AppBlock prefix="" title="Student / Parent App" />
      <AppBlock prefix="teacher" title="Teacher App" />

      {!readOnly && (
        <>
          {save.error && <p className="text-sm text-red-600">{errMsg(save.error)}</p>}
          {save.isSuccess && <p className="text-sm text-green-700">Saved.</p>}
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save App Settings'}
          </button>
        </>
      )}
    </div>
  );
}

// ───── Subscription Settings tab ────────────────────────────────────
function SubscriptionTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['platform-settings', 'subscription'], queryFn: PlatformSettingsApi.getSubscription });
  const [form, setForm] = useState<UpdateSubscriptionSettingsData>({});

  useEffect(() => {
    if (data) {
      setForm({
        additionalBillingDays: data.additionalBillingDays,
        planExpiryWarningDays: data.planExpiryWarningDays,
        defaultTrialDays: data.defaultTrialDays,
        defaultStudentLimit: data.defaultStudentLimit,
        defaultStaffLimit: data.defaultStaffLimit,
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => PlatformSettingsApi.updateSubscription(form),
    onSuccess: (updated) => qc.setQueryData(['platform-settings', 'subscription'], updated),
  });

  function set<K extends keyof UpdateSubscriptionSettingsData>(key: K, value: number) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  if (isLoading || !data) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-slate-500">
        Platform-wide fallbacks used when a plan doesn't specify its own value. Per-plan settings (under Plans) always take priority.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Additional Billing Days" hint="Grace period added after a subscription period ends">
          <Input type="number" value={form.additionalBillingDays ?? 0} onChange={(e) => set('additionalBillingDays', Number(e.target.value))} disabled={readOnly} />
        </Field>
        <Field label="Plan Expiry Warning (days)" hint="How many days before expiry to start warning">
          <Input type="number" value={form.planExpiryWarningDays ?? 7} onChange={(e) => set('planExpiryWarningDays', Number(e.target.value))} disabled={readOnly} />
        </Field>
        <Field label="Default Trial Days">
          <Input type="number" value={form.defaultTrialDays ?? 14} onChange={(e) => set('defaultTrialDays', Number(e.target.value))} disabled={readOnly} />
        </Field>
        <Field label="Default Student Limit" hint="-1 = unlimited">
          <Input type="number" value={form.defaultStudentLimit ?? 100} onChange={(e) => set('defaultStudentLimit', Number(e.target.value))} disabled={readOnly} />
        </Field>
        <Field label="Default Staff Limit" hint="-1 = unlimited">
          <Input type="number" value={form.defaultStaffLimit ?? 10} onChange={(e) => set('defaultStaffLimit', Number(e.target.value))} disabled={readOnly} />
        </Field>
      </div>

      {!readOnly && (
        <>
          {save.error && <p className="text-sm text-red-600">{errMsg(save.error)}</p>}
          {save.isSuccess && <p className="text-sm text-green-700">Saved.</p>}
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save Subscription Settings'}
          </button>
        </>
      )}
    </div>
  );
}

// ───── Email tab ───────────────────────────────────────────────────────
function EmailTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['platform-settings', 'email'], queryFn: PlatformSettingsApi.getEmail });
  const [form, setForm] = useState<UpdateEmailSettingsData>({});
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    if (data) setForm({ mailMailer: data.mailMailer, mailHost: data.mailHost ?? undefined, mailPort: data.mailPort, mailUsername: data.mailUsername ?? undefined, mailEncryption: data.mailEncryption, mailSendFrom: data.mailSendFrom ?? undefined });
  }, [data]);

  const save = useMutation({
    mutationFn: () => PlatformSettingsApi.updateEmail(form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-settings', 'email'] }),
  });

  const test = useMutation({
    mutationFn: () => PlatformSettingsApi.testEmail({ ...form, to: testTo }),
    onSuccess: () => setTestResult({ ok: true, message: `Test email sent to ${testTo}.` }),
    onError: (e) => setTestResult({ ok: false, message: errMsg(e) }),
  });

  function set<K extends keyof UpdateEmailSettingsData>(key: K, value: UpdateEmailSettingsData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  if (isLoading || !data) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">Status:</span>
        {data.emailVerified ? (
          <Badge tone="green">Verified</Badge>
        ) : (
          <Badge tone="amber">Not verified — send a test email after saving</Badge>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Mailer">
          <Input value={form.mailMailer ?? 'smtp'} onChange={(e) => set('mailMailer', e.target.value)} disabled={readOnly} />
        </Field>
        <Field label="Encryption">
          <Select value={form.mailEncryption ?? 'tls'} onChange={(e) => set('mailEncryption', e.target.value)} disabled={readOnly}>
            <option value="tls">TLS</option>
            <option value="ssl">SSL</option>
            <option value="none">None</option>
          </Select>
        </Field>
        <Field label="SMTP Host" required>
          <Input value={form.mailHost ?? ''} onChange={(e) => set('mailHost', e.target.value)} placeholder="smtp.mailtrap.io" disabled={readOnly} />
        </Field>
        <Field label="SMTP Port" required>
          <Input type="number" value={form.mailPort ?? 587} onChange={(e) => set('mailPort', Number(e.target.value))} disabled={readOnly} />
        </Field>
        <Field label="Username">
          <Input value={form.mailUsername ?? ''} onChange={(e) => set('mailUsername', e.target.value)} disabled={readOnly} />
        </Field>
        <Field label="Password" hint={data.hasPassword ? 'A password is already saved — leave blank to keep it.' : 'No password saved yet.'}>
          <Input type="password" value={form.mailPassword ?? ''} onChange={(e) => set('mailPassword', e.target.value)} placeholder={data.hasPassword ? '•••••••• (unchanged)' : ''} disabled={readOnly} />
        </Field>
        <Field label="Send From" className="sm:col-span-2">
          <Input type="email" value={form.mailSendFrom ?? ''} onChange={(e) => set('mailSendFrom', e.target.value)} placeholder="no-reply@edupro.app" disabled={readOnly} />
        </Field>
      </div>

      {!readOnly && (
        <>
          {save.error && <p className="text-sm text-red-600">{errMsg(save.error)}</p>}
          {save.isSuccess && <p className="text-sm text-green-700">Saved.</p>}
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save Email Settings'}
          </button>

          <div className="rounded-lg border border-slate-200 p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Send Test Email</h3>
            <div className="flex items-center gap-2">
              <Input className="!w-64" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" />
              <button
                className="btn-secondary flex items-center gap-1.5"
                disabled={!testTo.trim() || !form.mailHost || test.isPending}
                onClick={() => {
                  setTestResult(null);
                  test.mutate();
                }}
              >
                {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send Test Email
              </button>
            </div>
            {testResult && (
              <p className={`mt-2 flex items-center gap-1.5 text-sm ${testResult.ok ? 'text-green-700' : 'text-red-600'}`}>
                {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                {testResult.message}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ───── Content Pages tab ───────────────────────────────────────────────
function ContentPagesTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data: pages = [], isLoading } = useQuery({ queryKey: ['platform-settings', 'content-pages'], queryFn: PlatformSettingsApi.listContentPages });
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [form, setForm] = useState<{ title: string; body: string }>({ title: '', body: '' });

  const active = pages.find((p) => p.key === activeKey) ?? null;

  useEffect(() => {
    if (!activeKey && pages.length) setActiveKey(pages[0].key);
  }, [pages, activeKey]);

  useEffect(() => {
    if (active) setForm({ title: active.title, body: active.body });
  }, [active?.key]);

  const save = useMutation({
    mutationFn: () => PlatformSettingsApi.updateContentPage(activeKey!, form),
    onSuccess: (updated: ContentPage) => {
      qc.setQueryData<ContentPage[]>(['platform-settings', 'content-pages'], (prev) => prev?.map((p) => (p.key === updated.key ? updated : p)));
    },
  });

  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="flex gap-6">
      <div className="w-56 shrink-0 space-y-1">
        {pages.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`block w-full rounded px-3 py-2 text-left text-sm ${p.key === activeKey ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setActiveKey(p.key)}
          >
            {p.title}
          </button>
        ))}
      </div>

      {active && (
        <div className="max-w-3xl flex-1 space-y-4">
          <p className="text-sm text-slate-600">
            Public URL:{' '}
            <a href={`/page/${active.key}`} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
              {window.location.origin}/page/{active.key}
            </a>
          </p>
          <Field label="Page Title">
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} disabled={readOnly} />
          </Field>
          <Field label="Body">
            <Editor
              licenseKey="gpl"
              value={form.body}
              disabled={readOnly}
              onEditorChange={(body) => setForm((f) => ({ ...f, body }))}
              init={{
                height: 420,
                menubar: 'file edit view tools',
                toolbar:
                  'undo redo | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | blockquote | code',
                plugins: ['lists', 'link', 'code'],
                skin_url: '/tinymce/skins/ui/oxide',
                content_css: '/tinymce/skins/content/default/content.min.css',
              }}
            />
          </Field>
          {!readOnly && (
            <>
              {save.error && <p className="text-sm text-red-600">{errMsg(save.error)}</p>}
              {save.isSuccess && <p className="text-sm text-green-700">Saved.</p>}
              <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save Page'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ───── Guidance tab — real shape: a Name + a Link, nothing else ─────
function GuidanceTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data: entries = [], isLoading } = useQuery({ queryKey: ['platform-settings', 'guidance'], queryFn: PlatformSettingsApi.listGuidance });
  const [newName, setNewName] = useState('');
  const [newLink, setNewLink] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<GuidanceEntry | null>(null);
  const [editForm, setEditForm] = useState({ name: '', link: '' });
  const [confirmDelete, setConfirmDelete] = useState<GuidanceEntry | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['platform-settings', 'guidance'] });

  const create = useMutation({
    mutationFn: () => PlatformSettingsApi.createGuidance({ name: newName, link: newLink }),
    onSuccess: () => {
      setNewName('');
      setNewLink('');
      invalidate();
    },
  });

  const update = useMutation({
    mutationFn: () => PlatformSettingsApi.updateGuidance(editing!.id, editForm),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => PlatformSettingsApi.removeGuidance(id),
    onSuccess: () => {
      setConfirmDelete(null);
      invalidate();
    },
  });

  const filtered = entries.filter((e) => {
    const q = search.trim().toLowerCase();
    return !q || e.name.toLowerCase().includes(q) || e.link.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      {!readOnly && (
        <div className="card p-5">
          <h3 className="mb-4 text-base font-semibold text-slate-900">Create Guidance</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Name" required>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name" />
            </Field>
            <Field label="Link" required>
              <Input value={newLink} onChange={(e) => setNewLink(e.target.value)} placeholder="Link" />
            </Field>
          </div>
          {create.error && <p className="mt-2 text-sm text-red-600">{errMsg(create.error)}</p>}
          <button className="btn-primary mt-4" disabled={!newName.trim() || !newLink.trim() || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      )}

      <div className="card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">List Guidance</h3>
          <Input className="!w-56" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" />
        </div>
        <DataTable<GuidanceEntry>
          columns={[
            { key: 'no', header: 'No.', render: (_r) => filtered.indexOf(_r) + 1, width: '60px' },
            { key: 'name', header: 'Name' },
            {
              key: 'link',
              header: 'Link',
              render: (r) => (
                <a href={r.link} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">
                  {r.link}
                </a>
              ),
            },
          ]}
          rows={filtered}
          getRowId={(r) => r.id}
          isLoading={isLoading}
          emptyMessage="No matching records found"
          actions={
            readOnly
              ? undefined
              : (r) => (
                  <>
                    <button
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                      onClick={() => {
                        setEditing(r);
                        setEditForm({ name: r.name, link: r.link });
                      }}
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setConfirmDelete(r)} title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )
          }
        />
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Guidance"
        size="sm"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setEditing(null)}>
              Cancel
            </button>
            <button className="btn-primary" disabled={update.isPending} onClick={() => update.mutate()}>
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name" required>
            <Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Link" required>
            <Input value={editForm.link} onChange={(e) => setEditForm((f) => ({ ...f, link: e.target.value }))} />
          </Field>
          {update.error && <p className="text-sm text-red-600">{errMsg(update.error)}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        loading={remove.isPending}
        title="Delete guidance entry?"
        message={`"${confirmDelete?.name}" will be removed.`}
        confirmText="Delete"
      />
    </div>
  );
}

/** No real translation-file format exists yet (no i18n system consumes these packs) — this is a reasonable starter template, not a format read anywhere. */
function downloadSampleLanguageFile() {
  const sample = { welcome: 'Welcome', dashboard: 'Dashboard', logout: 'Logout' };
  const blob = new Blob([JSON.stringify(sample, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'language-sample.json';
  a.click();
  URL.revokeObjectURL(url);
}

// ───── Language Settings tab ───────────────────────────────────────
function LanguageTab({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const { data: packs = [], isLoading } = useQuery({ queryKey: ['platform-settings', 'languages'], queryFn: PlatformSettingsApi.listLanguages });
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newIsRtl, setNewIsRtl] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | undefined>();
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<LanguagePack | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['platform-settings', 'languages'] });

  const create = useMutation({
    mutationFn: () => PlatformSettingsApi.createLanguage({ name: newName, code: newCode, fileUrl, isRtl: newIsRtl }),
    onSuccess: () => {
      setNewName('');
      setNewCode('');
      setNewIsRtl(false);
      setFileUrl(undefined);
      setFileName('');
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => PlatformSettingsApi.removeLanguage(id),
    onSuccess: () => {
      setConfirmDelete(null);
      invalidate();
    },
  });

  async function handleUpload(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await PlatformSettingsApi.uploadLanguageFile(file);
      setFileUrl(res.url);
      setFileName(file.name);
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  const filtered = packs.filter((p) => {
    const q = search.trim().toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      {!readOnly && (
        <div className="card p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Language Name" required>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Language Name" />
            </Field>
            <Field label="Language Code" required>
              <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="Language Code" />
            </Field>
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Language File <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                readOnly
                value={fileName}
                placeholder="Language File"
                className="max-w-sm cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.csv,.txt"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              />
              <button type="button" className="btn-primary" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              <button type="button" className="btn-secondary inline-flex items-center gap-1.5" onClick={downloadSampleLanguageFile}>
                <Download className="h-4 w-4" /> Download Sample File
              </button>
            </div>
            {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={newIsRtl} onChange={(e) => setNewIsRtl(e.target.checked)} />
            Is RTL
          </label>

          {create.error && <p className="mt-2 text-sm text-red-600">{errMsg(create.error)}</p>}
          <button
            className="btn-primary mt-4"
            disabled={!newName.trim() || !newCode.trim() || !fileUrl || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      )}

      <div className="card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">List Language</h3>
          <Input className="!w-56" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" />
        </div>
        <DataTable<LanguagePack>
          columns={[
            { key: 'no', header: 'No.', render: (r) => filtered.indexOf(r) + 1, width: '60px' },
            { key: 'name', header: 'Name' },
            { key: 'code', header: 'Code', render: (r) => <span className="font-mono text-xs">{r.code}</span> },
            { key: 'isRtl', header: 'Is RTL', render: (r) => <Badge tone={r.isRtl ? 'green' : 'red'}>{r.isRtl ? 'Yes' : 'No'}</Badge> },
          ]}
          rows={filtered}
          getRowId={(r) => r.id}
          isLoading={isLoading}
          emptyMessage="No matching records found"
          actions={
            readOnly
              ? undefined
              : (r) => (
                  <button className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setConfirmDelete(r)} title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )
          }
        />
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        loading={remove.isPending}
        title="Delete language pack?"
        message={`"${confirmDelete?.name}" will be removed.`}
        confirmText="Delete"
      />
    </div>
  );
}

// ───── Page ────────────────────────────────────────────────────────────
/**
 * `readOnly` — org-tier callers pass this (backend only allows writes via
 * `SuperadminGuard`), same pattern as `MaterialItemsPage`/`ProgramItemsPage`.
 */
export function SettingsPage({ readOnly = false }: { readOnly?: boolean } = {}) {
  const [tab, setTab] = useState<Tab>('General');

  return (
    <div>
      <PageHeader
        title="Platform Settings"
        description={
          readOnly
            ? "System-wide settings that apply across the entire deployment — not per-organization or per-school. Read-only here; only Super Admin can change these."
            : 'System-wide settings that apply across the entire deployment — not per-organization or per-school.'
        }
      />

      <div className="mb-6 border-b border-slate-200">
        <nav className="-mb-px flex gap-6">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              className={`border-b-2 px-1 py-2 text-sm font-medium ${t === tab ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'General' && <GeneralTab readOnly={readOnly} />}
      {tab === 'App' && <AppTab readOnly={readOnly} />}
      {tab === 'Subscription' && <SubscriptionTab readOnly={readOnly} />}
      {tab === 'Email' && <EmailTab readOnly={readOnly} />}
      {tab === 'Content Pages' && <ContentPagesTab readOnly={readOnly} />}
      {tab === 'Guidance' && <GuidanceTab readOnly={readOnly} />}
      {tab === 'Languages' && <LanguageTab readOnly={readOnly} />}
    </div>
  );
}
